import TwitchPlayer from "@/components/TwitchPlayer";
import { PLAYER_RUNESTONE_CABINET_QUERY } from "@/graphql/queries/profileQueries";
import { LIVE_STREAMS_SUBSCRIPTION } from "@/graphql/subscriptions/streamTeamSubscriptions";
import { useAuth } from "@/hooks/auth/useAuth";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Instagram,
    X,
    Youtube,
} from "lucide-react";
import { useState } from "react";
import { FaDiscord, FaTiktok } from "react-icons/fa";
import { Navigate, useParams } from "react-router-dom";

const STREAMER_PROFILE_QUERY = gql`
    query StreamerProfile($twitchUsername: String!) {
        streamTeamMemberByUsername(twitchUsername: $twitchUsername) {
            id
            player {
                id
                name
                avatarUrl
            }
            bio
            twitchUsername
            twitterUrl
            youtubeUrl
            discordUrl
            instagramUrl
            tiktokUrl
            twitchAuth {
                profileImageUrl
            }
            runestones {
                imageUrl
            }
            twitchClips {
                id
                clipId
                title
                url
                embedUrl
                thumbnailUrl
                viewCount
                createdAtTwitch
                creatorName
                duration
            }
        }
        liveStreams {
            id
            streamTeamMember {
                id
                twitchUsername
            }
            isLive
            streamTitle
            gameName
            viewerCount
            startedAt
        }
        streamSchedules {
            id
            streamTeamMember {
                id
                twitchUsername
            }
            title
            category
            startTime
            endTime
        }
        streamTeamChannelRpLeaderboard(limit: 200, offset: 0) {
            streamTeamMember {
                id
                twitchUsername
            }
            totalRavenguardPoints
        }
    }
`;

const STREAMER_RP_EARNERS_QUERY = gql`
    query StreamerRpEarners($memberId: UUID!) {
        streamTeamMemberRpEarnersLeaderboard(
            memberId: $memberId
            limit: 10
            offset: 0
        ) {
            player {
                id
                name
                shield {
                    imageUrl
                    imageWithTagsUrl
                }
            }
            totalRavenguardPoints
        }
    }
`;

interface StreamerProfile {
    id: string;
    player: {
        id: string;
        name: string;
        avatarUrl: string | null;
    };
    bio: string;
    twitchUsername: string;
    twitterUrl: string;
    youtubeUrl: string;
    discordUrl: string;
    instagramUrl: string;
    tiktokUrl: string;
    twitchAuth?: {
        profileImageUrl: string;
    } | null;
    runestones?: { imageUrl: string }[];
    twitchClips: TwitchClip[];
}

interface TwitchClip {
    id: string;
    clipId: string;
    title: string;
    url: string;
    embedUrl: string;
    thumbnailUrl: string;
    viewCount: number;
    createdAtTwitch: string;
    creatorName: string;
    duration: number;
}

interface LiveStream {
    id: string;
    streamTeamMember: {
        id: string;
        twitchUsername: string;
    };
    isLive: boolean;
    streamTitle: string;
    gameName: string;
    viewerCount: number;
    startedAt: string;
}

interface StreamSchedule {
    id: string;
    streamTeamMember: {
        id: string;
        twitchUsername: string;
    };
    title: string;
    category: string;
    startTime: string;
    endTime: string | null;
}

interface ChannelRpLeaderboardEntry {
    streamTeamMember: {
        id: string;
        twitchUsername: string;
    };
    totalRavenguardPoints: number;
}

interface PlayerRunestoneCabinetEntry {
    hasRedeemed: boolean;
    redemptionCount: number;
    ravenguardPoints: number;
    streamTeamMember: {
        id: string;
    };
}

interface RpEarnerLeaderboardEntry {
    player: {
        id: string;
        name: string;
        shield?: {
            imageUrl?: string | null;
            imageWithTagsUrl?: string | null;
        } | null;
    };
    totalRavenguardPoints: number;
}

export default function StreamerProfilePage() {
    const { username } = useParams<{ username: string }>();
    const { isAuthenticated } = useAuth();
    const [clipsPage, setClipsPage] = useState(0);
    const [selectedClip, setSelectedClip] = useState<TwitchClip | null>(null);
    const clipsPerPage = 4;

    const { loading, error, data, refetch } = useQuery<{
        streamTeamMemberByUsername: StreamerProfile;
        liveStreams: LiveStream[];
        streamSchedules: StreamSchedule[];
        streamTeamChannelRpLeaderboard: ChannelRpLeaderboardEntry[];
    }>(STREAMER_PROFILE_QUERY, {
        variables: { twitchUsername: username },
        fetchPolicy: "cache-and-network",
    });

    const { data: rpEarnersData } = useQuery<{
        streamTeamMemberRpEarnersLeaderboard: RpEarnerLeaderboardEntry[];
    }>(STREAMER_RP_EARNERS_QUERY, {
        variables: { memberId: data?.streamTeamMemberByUsername?.id || "" },
        fetchPolicy: "cache-and-network",
        skip: !data?.streamTeamMemberByUsername?.id,
    });

    useSubscription(LIVE_STREAMS_SUBSCRIPTION, {
        onData: () => {
            refetch();
        },
    });

    const { data: viewerCabinetData, loading: viewerCabinetLoading } =
        useQuery<{ playerRunestoneCabinet: PlayerRunestoneCabinetEntry[] }>(
            PLAYER_RUNESTONE_CABINET_QUERY,
            {
                fetchPolicy: "cache-and-network",
                skip: !isAuthenticated,
            },
        );

    if (loading) {
        return (
            <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
                <p className="font-garamond text-center text-zinc-400">
                    Loading profile...
                </p>
            </div>
        );
    }

    if (error || !data?.streamTeamMemberByUsername) {
        return <Navigate to="/team" replace />;
    }

    const profile = data.streamTeamMemberByUsername;
    const avatarUrl =
        profile.twitchAuth?.profileImageUrl || profile.player.avatarUrl;

    // Check if this streamer is currently live
    const liveStream = data.liveStreams.find(
        (stream) =>
            stream.streamTeamMember.twitchUsername === profile.twitchUsername &&
            stream.isLive,
    );

    // Get upcoming schedules for this streamer
    const now = new Date();
    const upcomingSchedules = data.streamSchedules
        .filter(
            (schedule) =>
                schedule.streamTeamMember.twitchUsername ===
                    profile.twitchUsername &&
                new Date(schedule.startTime) >= now,
        )
        .sort(
            (a, b) =>
                new Date(a.startTime).getTime() -
                new Date(b.startTime).getTime(),
        )
        .slice(0, 5);

    // Social links
    const socialLinks = [
        {
            url: profile.twitterUrl,
            icon: X,
            name: "X",
            color: "hover:text-blue-400",
        },
        {
            url: profile.youtubeUrl,
            icon: Youtube,
            name: "YouTube",
            color: "hover:text-red-500",
        },
        {
            url: profile.discordUrl,
            icon: FaDiscord,
            name: "Discord",
            color: "hover:text-indigo-400",
        },
        {
            url: profile.instagramUrl,
            icon: Instagram,
            name: "Instagram",
            color: "hover:text-pink-400",
        },
        {
            url: profile.tiktokUrl,
            icon: FaTiktok,
            name: "TikTok",
            color: "hover:text-cyan-400",
        },
    ].filter((link) => link.url);

    const viewerProgress = viewerCabinetData?.playerRunestoneCabinet?.find(
        (entry) => entry.streamTeamMember.id === profile.id,
    );

    const channelRpTotal =
        data.streamTeamChannelRpLeaderboard?.find(
            (entry) =>
                entry.streamTeamMember.twitchUsername.toLowerCase() ===
                profile.twitchUsername.toLowerCase(),
        )?.totalRavenguardPoints ?? 0;

    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            {/* Header Section */}
            <div className="mb-6 flex flex-col items-center gap-4 sm:mb-8 sm:gap-6 md:flex-row">
                {avatarUrl ? (
                    <img
                        src={avatarUrl}
                        alt={profile.player.name}
                        className="h-24 w-24 rounded-full border-4 border-yellow-500 object-cover sm:h-32 sm:w-32"
                    />
                ) : (
                    <div className="bg-brand flex h-24 w-24 items-center justify-center rounded-full border-4 border-yellow-500 text-4xl font-bold text-zinc-900 sm:h-32 sm:w-32 sm:text-5xl">
                        {profile.player.name.charAt(0).toUpperCase()}
                    </div>
                )}
                <div className="flex-1 text-center md:text-left">
                    <h1 className="font-prviking text-brand mb-2 text-3xl sm:text-4xl lg:text-5xl">
                        {convertLetters(profile.player.name)}
                    </h1>
                    <p className="font-garamond mb-3 text-base text-zinc-300 sm:mb-4 sm:text-lg lg:text-xl">
                        {profile.bio || "No bio available."}
                    </p>
                    <div className="mb-3 inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900/80 px-3 py-2 sm:mb-4 sm:px-4">
                        <span className="font-garamond text-xs tracking-wide text-zinc-400 uppercase sm:text-sm">
                            RAVENGUARD POINTS
                        </span>
                        <span className="font-prviking text-brand text-lg sm:text-xl">
                            {convertLetters(channelRpTotal.toLocaleString())}
                        </span>
                    </div>
                    {socialLinks.length > 0 && (
                        <div className="flex justify-center gap-3 sm:gap-4 md:justify-start">
                            {socialLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`text-zinc-400 transition-colors ${link.color}`}
                                    title={link.name}
                                >
                                    <link.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                                </a>
                            ))}
                        </div>
                    )}
                </div>
                {profile.runestones && profile.runestones.length > 0 && (
                    <div className="flex shrink-0 flex-col items-center">
                        <img
                            src={profile.runestones[0].imageUrl}
                            alt="Runestone"
                            className="h-24 w-24 object-contain sm:h-32 sm:w-32"
                        />
                        {isAuthenticated && (
                            <div className="font-garamond mt-2 text-center text-xs text-zinc-300 sm:text-sm">
                                {viewerCabinetLoading ? (
                                    <p className="text-zinc-400">
                                        Loading progress...
                                    </p>
                                ) : (
                                    <>
                                        <p>
                                            <span className="text-zinc-400">
                                                RP:
                                            </span>{" "}
                                            <span className="font-semibold text-yellow-300">
                                                {(
                                                    viewerProgress?.ravenguardPoints ??
                                                    0
                                                ).toLocaleString()}
                                            </span>
                                        </p>
                                        <p>
                                            <span className="text-zinc-400">
                                                Redemptions:
                                            </span>{" "}
                                            <span className="font-semibold text-yellow-300">
                                                {(
                                                    viewerProgress?.redemptionCount ??
                                                    0
                                                ).toLocaleString()}
                                            </span>
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Live Stream Section */}
            {liveStream ? (
                <div className="mb-6 sm:mb-8">
                    <h2 className="font-prviking text-brand mb-3 text-2xl sm:mb-4 sm:text-3xl">
                        {convertLetters("Live Now")}
                    </h2>
                    <div className="space-y-4 overflow-hidden rounded-xl border-2 border-red-500 bg-zinc-900 p-3 sm:p-4">
                        {/* Stream info */}
                        <div>
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center rounded-full bg-red-500 px-3 py-1 text-xs font-bold text-white">
                                    LIVE
                                </span>
                                <span className="font-garamond text-sm text-zinc-400 sm:text-base">
                                    {liveStream.viewerCount} viewers
                                </span>
                            </div>
                            <h3 className="font-garamond mb-1 text-lg font-bold text-zinc-200 sm:text-xl">
                                {liveStream.streamTitle}
                            </h3>
                            <p className="font-garamond text-sm text-zinc-400 sm:text-base">
                                {liveStream.gameName}
                            </p>
                        </div>

                        {/* Chat embed */}
                        <div className="h-225 flex-1 overflow-hidden rounded-lg bg-zinc-900">
                            <TwitchPlayer
                                channel={profile.twitchUsername}
                                autoplay={false}
                                muted={false}
                                layout="video-with-chat"
                                className="h-full w-full"
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="border-brand_stream_team mb-6 rounded-xl border-2 bg-zinc-900 p-4 text-center sm:mb-8 sm:p-6">
                    <p className="font-garamond text-sm text-zinc-400 sm:text-base">
                        {profile.player.name} is currently offline
                    </p>
                    <a
                        href={`https://twitch.tv/${profile.twitchUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-purple-700 sm:mt-4 sm:px-6 sm:py-3 sm:text-base"
                    >
                        <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="font-garamond">
                            Visit Twitch Channel
                        </span>
                    </a>
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2">
                {/* Upcoming Streams */}
                <div>
                    <h2 className="font-prviking text-brand mb-3 text-2xl sm:mb-4 sm:text-3xl">
                        {convertLetters("Upcoming Streams")}
                    </h2>
                    {upcomingSchedules.length > 0 ? (
                        <div className="space-y-3 sm:space-y-4">
                            {upcomingSchedules.map((schedule) => {
                                const startTime = new Date(schedule.startTime);
                                return (
                                    <div
                                        key={schedule.id}
                                        className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-3 transition-all hover:border-yellow-500 sm:p-4"
                                    >
                                        <div className="font-garamond mb-2 text-xs text-zinc-400 sm:text-sm">
                                            {startTime.toLocaleDateString(
                                                "en-US",
                                                {
                                                    weekday: "long",
                                                    month: "long",
                                                    day: "numeric",
                                                },
                                            )}{" "}
                                            at{" "}
                                            {startTime.toLocaleTimeString(
                                                "en-US",
                                                {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                },
                                            )}
                                        </div>
                                        <h3 className="font-garamond mb-1 text-sm font-bold text-zinc-200 sm:text-base">
                                            {schedule.title ||
                                                "Untitled Stream"}
                                        </h3>
                                        {schedule.category && (
                                            <p className="font-garamond text-xs text-zinc-400 sm:text-sm">
                                                {schedule.category}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4 text-center sm:p-6">
                            <p className="font-garamond text-sm text-zinc-400 sm:text-base">
                                No upcoming streams scheduled
                            </p>
                        </div>
                    )}
                </div>

                {/* Recent Clips */}
                <div>
                    <h2 className="font-prviking text-brand mb-3 text-2xl sm:mb-4 sm:text-3xl">
                        {convertLetters("Recent Clips")}
                    </h2>
                    {profile.twitchClips.length > 0 ? (
                        <div>
                            <div className="grid grid-cols-2 gap-4">
                                {profile.twitchClips
                                    .slice(
                                        clipsPage * clipsPerPage,
                                        (clipsPage + 1) * clipsPerPage,
                                    )
                                    .map((clip) => (
                                        <button
                                            key={clip.id}
                                            onClick={() =>
                                                setSelectedClip(clip)
                                            }
                                            className="group border-brand_stream_team block overflow-hidden rounded-lg border-2 bg-zinc-900 text-left transition-all hover:border-yellow-500"
                                        >
                                            <div className="relative">
                                                <img
                                                    src={clip.thumbnailUrl}
                                                    alt={clip.title}
                                                    className="aspect-video w-full object-cover"
                                                />
                                                <div className="absolute right-2 bottom-2 rounded bg-black/75 px-2 py-1 text-xs font-bold text-white">
                                                    {Math.floor(clip.duration)}s
                                                </div>
                                            </div>
                                            <div className="p-3">
                                                <h3 className="font-garamond mb-1 line-clamp-2 text-sm font-bold text-zinc-200 group-hover:text-yellow-500">
                                                    {clip.title}
                                                </h3>
                                                <div className="font-garamond flex items-center justify-between text-xs text-zinc-400">
                                                    <span className="truncate">
                                                        By {clip.creatorName}
                                                    </span>
                                                    <span className="ml-2 shrink-0">
                                                        {clip.viewCount.toLocaleString()}{" "}
                                                        views
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                            </div>

                            {profile.twitchClips.length > clipsPerPage && (
                                <div className="mt-4 flex items-center justify-center gap-4">
                                    <button
                                        onClick={() =>
                                            setClipsPage((prev) =>
                                                Math.max(0, prev - 1),
                                            )
                                        }
                                        disabled={clipsPage === 0}
                                        className="rounded-lg bg-zinc-700 p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-zinc-700 disabled:hover:text-zinc-300"
                                        aria-label="Previous clips"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <div className="font-garamond text-sm text-zinc-300">
                                        Page {clipsPage + 1} of{" "}
                                        {Math.ceil(
                                            profile.twitchClips.length /
                                                clipsPerPage,
                                        )}
                                    </div>
                                    <button
                                        onClick={() =>
                                            setClipsPage((prev) =>
                                                Math.min(
                                                    Math.ceil(
                                                        profile.twitchClips
                                                            .length /
                                                            clipsPerPage,
                                                    ) - 1,
                                                    prev + 1,
                                                ),
                                            )
                                        }
                                        disabled={
                                            clipsPage >=
                                            Math.ceil(
                                                profile.twitchClips.length /
                                                    clipsPerPage,
                                            ) -
                                                1
                                        }
                                        className="rounded-lg bg-zinc-700 p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-zinc-700 disabled:hover:text-zinc-300"
                                        aria-label="Next clips"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-6 text-center">
                            <p className="font-garamond text-zinc-400">
                                No recent clips available
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Top 10 RP Earners Leaderboard */}
            <div className="mt-6 sm:mt-8">
                <h2 className="font-prviking text-brand mb-3 text-2xl sm:mb-4 sm:text-3xl">
                    {convertLetters("Top 10 RP Earners")}
                </h2>
                {rpEarnersData?.streamTeamMemberRpEarnersLeaderboard &&
                rpEarnersData.streamTeamMemberRpEarnersLeaderboard.length >
                    0 ? (
                    <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4 sm:p-6">
                        <div className="space-y-3">
                            {rpEarnersData.streamTeamMemberRpEarnersLeaderboard.map(
                                (entry, index) => {
                                    const imageUrl =
                                        entry.player.shield?.imageWithTagsUrl ||
                                        entry.player.shield?.imageUrl ||
                                        "/no-shield.png";
                                    return (
                                        <div
                                            key={entry.player.id}
                                            className="flex items-center justify-between rounded-lg border border-zinc-700 bg-zinc-950/50 px-4 py-3 transition-colors hover:border-yellow-500"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="font-prviking text-brand w-6 text-right text-lg sm:text-xl">
                                                    {index + 1}
                                                </div>
                                                <img
                                                    src={imageUrl}
                                                    alt={entry.player.name}
                                                    className="h-8 w-8 rounded-full border border-zinc-600 object-cover"
                                                />
                                                <span className="font-garamond text-sm text-zinc-200 sm:text-base">
                                                    {entry.player.name}
                                                </span>
                                            </div>
                                            <div className="font-prviking text-brand text-right text-lg sm:text-xl">
                                                {entry.totalRavenguardPoints.toLocaleString()}{" "}
                                                <span className="text-xs text-zinc-400 sm:text-sm">
                                                    RP
                                                </span>
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4 text-center sm:p-6">
                        <p className="font-garamond text-sm text-zinc-400 sm:text-base">
                            No RP earners yet
                        </p>
                    </div>
                )}
            </div>

            {/* Clip Modal */}
            {selectedClip && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setSelectedClip(null)}
                >
                    <div
                        className="relative w-full max-w-5xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelectedClip(null)}
                            className="absolute -top-12 right-0 rounded-lg bg-zinc-700 p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500"
                            aria-label="Close"
                        >
                            <ChevronRight className="h-6 w-6 rotate-45" />
                        </button>
                        <div className="border-brand_stream_team overflow-hidden rounded-xl border-2 bg-zinc-900">
                            <iframe
                                src={`${selectedClip.embedUrl}&parent=${window.location.hostname}&autoplay=true`}
                                height="500"
                                width="100%"
                                allowFullScreen
                                className="aspect-video w-full"
                                title={selectedClip.title}
                            />
                            <div className="p-4">
                                <h3 className="font-garamond mb-2 text-xl font-bold text-zinc-200">
                                    {selectedClip.title}
                                </h3>
                                <div className="font-garamond flex items-center justify-between text-sm text-zinc-400">
                                    <span>By {selectedClip.creatorName}</span>
                                    <span>
                                        {selectedClip.viewCount.toLocaleString()}{" "}
                                        views
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
